import React from "react";

export default function FboSearchApiDoc() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Header */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            FBO API — Fetch FBOs
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            Retrieve active Fixed Base Operators (FBOs) filtered by FBO name,
            airport code, or airport ID. Supports pagination. We have FBOs
            associated with airports. By passing an airport filter (either
            airport code or airport ID), you can narrow down the list of FBOs to
            those that operate at a specific airport.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Method
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                POST (list/filter)
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Auth
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">JWT</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Endpoint
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                /api/fbo-search/
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Overview
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            This endpoint returns active FBOs filtered by FBO name and
            optionally scoped to a specific airport code or airport ID. Results
            are always case-insensitive and partial matches are supported.
          </p>

          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              <span className="font-semibold">Search:</span> filters by FBO name
              using case-insensitive matching.
            </li>
            <li>
              <span className="font-semibold">Airport filter:</span> restrict
              results by <code className="font-mono">airport_id</code>.
            </li>
            <li>
              <span className="font-semibold">Airport code filter:</span>{" "}
              restrict results by{" "}
              <code className="font-mono">airport_code</code> (exact match on
              airport initials).
            </li>
          </ul>
        </section>

        {/* Request */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Request
          </h2>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">HTTP</div>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <span className="font-semibold">Method:</span>{" "}
                  <code className="font-mono">POST</code>
                </li>
                <li>
                  <span className="font-semibold">URL:</span>{" "}
                  <code className="font-mono">/api/fbo-search/</code>
                </li>
                <li>
                  <span className="font-semibold">Headers:</span>
                  <br />
                  <code className="font-mono">
                    Authorization: JWT your_token
                  </code>
                  <br />
                  <code className="font-mono">
                    Content-Type: application/json
                  </code>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Body schema
              </div>
              <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">name</code>{" "}
                  <span className="ml-1 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>{" "}
                  — <span className="font-mono">string</span>. Partial match on
                  FBO name.
                </li>
                <li>
                  <code className="font-mono">airport_id</code>{" "}
                  <span className="ml-1 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>{" "}
                  — <span className="font-mono">number</span>. Filters FBOs
                  available at a specific airport.
                </li>
                <li>
                  <code className="font-mono">airport_code</code>{" "}
                  <span className="ml-1 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>{" "}
                  — <span className="font-mono">string</span>. Filters by
                  airport initials (exact match).
                </li>
              </ul>
            </div>
          </div>

          {/* Request Example */}
          <div className="not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md">
            <div className="flex border-b border-zinc-700 bg-zinc-800 px-4">
              <h3 className="pt-3 text-sm font-semibold text-white">Request</h3>
            </div>

            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <span className="font-mono text-xs text-emerald-400">POST</span>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                /api/fbo-search/
              </span>
            </div>

            <pre className="p-4 text-xs text-white">
              {`POST https://api-livetakeoff.herokuapp.com/api/fbo-search/
Authorization: JWT your_access_token
Content-Type: application/json

{
  "name": "signature",
  "airport_code": "MIA"
}`}
            </pre>
          </div>
        </section>

        {/* Response */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Response
          </h2>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">Envelope</div>
            <ul className="mt-2 text-sm text-slate-700 space-y-1">
              <li>
                <code>count</code> — total items
              </li>
              <li>
                <code>next</code> — next page
              </li>
              <li>
                <code>previous</code> — previous page
              </li>
              <li>
                <code>results</code> — array of FBOs
              </li>
            </ul>
          </div>

          {/* Response Example */}
          <div className="not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md">
            <div className="flex border-b border-zinc-700 bg-zinc-800 px-4">
              <h3 className="pt-3 text-sm font-semibold text-white">
                Response
              </h3>
            </div>

            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <span className="font-mono text-xs text-emerald-400">200</span>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                application/json
              </span>
            </div>

            <pre className="p-4 text-xs text-white">
              {`{
  "count": 1,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": 10,
      "name": "Signature Aviation",
      "fee": "150.00",
      "fee_percentage": false
    }
  ]
}`}
            </pre>
          </div>
        </section>

        {/* Rules */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Validation & rules
          </h2>

          <ul className="mt-3 list-disc pl-5 text-slate-700 space-y-2">
            <li>
              Request body is optional. Empty body returns all active FBOs.
            </li>
            <li>
              <code>airport_id</code> and <code>airport_code</code> can be used
              together to further narrow results.
            </li>
            <li>
              Sorting by fee supports null-safe ordering (nulls first/last).
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}
